import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminFormFieldError,
  AdminFormDropdownChevron,
  AdminSupportingText,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
} from '../shared/adminUx';
import { isTeamOwnerActor } from '../../../data/teamConfig';
import {
  DURATION_OPTIONS,
  FRAUD_SECURITY_ROUTE,
  MESSAGES,
  MODE_OPTIONS,
  PRIORITY_OPTIONS,
  PROTECTION_ACTION,
  SEVERITY_HINT_OPTIONS,
  availableSources,
  buildSampleFraudSecurityRules,
  createEmptyFraudSecurityForm,
  normalizeFraudSecurityForm,
  responsePeriodFor,
  ruleFingerprint,
} from '../../../data/fraudSecurityConfig';
import {
  buildFraudSecurityConfigActor,
  mergeFraudSecurityRulesAfterSave,
} from '../../../data/fraudSecurityRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

const FraudSecurityRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const canAccess = isTeamOwnerActor(auth);
  const actor = useMemo(() => buildFraudSecurityConfigActor(auth), [auth]);
  const saveLockRef = useRef(false);
  const openedFingerprintRef = useRef('');

  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleFraudSecurityRules(),
    [existingRulesFromState],
  );

  const resolvedSource = useMemo(() => {
    if (isCreate) return createEmptyFraudSecurityForm();
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return normalizeFraudSecurityForm(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) return normalizeFraudSecurityForm(found);
    }
    return createEmptyFraudSecurityForm();
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(resolvedSource);
    setErrors({});
    saveLockRef.current = false;
    openedFingerprintRef.current = isCreate ? '' : ruleFingerprint(resolvedSource);
  }, [resolvedSource, isCreate]);

  useEffect(() => {
    if (!isView) return undefined;
    const id = resolvedSource?.id;
    navigate(id ? `${FRAUD_SECURITY_ROUTE}?view=${encodeURIComponent(id)}` : FRAUD_SECURITY_ROUTE, {
      replace: true,
      state: { rules: allRules },
    });
    return undefined;
  }, [isView, resolvedSource, allRules, navigate]);

  const sourceOptions = useMemo(() => availableSources(), []);

  const validationMessages = useMemo(() => {
    const seen = new Set();
    const list = [];
    Object.values(errors || {}).forEach((message) => {
      if (!message || seen.has(message)) return;
      seen.add(message);
      list.push(message);
    });
    return list;
  }, [errors]);

  const clearError = (key) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChange = (field, value) => {
    clearError(field);
    setFormData((prev) => {
      const next = { ...prev, [field]: value, permanentBan: true };
      if (field === 'restriction' && value === 'Unavailable') next.duration = '';
      if (field === 'protection' && value === 'Unavailable') next.signOutSessions = false;
      return next;
    });
  };

  const handleCancel = () => {
    navigate(FRAUD_SECURITY_ROUTE, { state: { rules: allRules } });
  };

  const handleSave = () => {
    if (saveLockRef.current) return;
    const result = mergeFraudSecurityRulesAfterSave(allRules, formData, actor, {
      isCreate,
      openedRuleFingerprint: openedFingerprintRef.current,
    });
    if (!result.success) {
      setErrors(result.errors || {});
      return;
    }
    saveLockRef.current = true;
    navigate(FRAUD_SECURITY_ROUTE, { state: { rules: result.rules } });
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Fraud & Security</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">{MESSAGES.accessDenied}</AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle>Fraud & Security</AdminPageTitle>
      </AdminLayout>
    );
  }

  if (!isCreate && !formData.id) {
    return (
      <AdminLayout>
        <AdminPageTitle>Fraud & Security Rule not found</AdminPageTitle>
      </AdminLayout>
    );
  }

  const restrictionApplies = formData.restriction === 'Optional' || formData.restriction === 'Required';
  const protectionApplies = formData.protection === 'Optional' || formData.protection === 'Required';

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add New' : 'Edit Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
          {!isCreate ? (
            <AdminConfigLockedField
              label="Rule ID"
              value={formData.id}
              mono
              isView={false}
            />
          ) : null}
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Rule Name <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              id="fsr-rule-name"
              type="text"
              value={formData.ruleName || ''}
              onChange={(e) => handleChange('ruleName', e.target.value)}
              className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
            />
            {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
          </div>
        </div>

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
          {isCreate ? (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Source <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <div className="relative w-full min-w-0">
                <select
                  id="fsr-source"
                  value={formData.source || ''}
                  onChange={(e) => handleChange('source', e.target.value)}
                  className={adminFormDetailSelectClasses(false, Boolean(errors.source))}
                >
                  <option value="">Select Source</option>
                  {sourceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
              {errors.source ? <AdminFormFieldError>{errors.source}</AdminFormFieldError> : null}
            </div>
          ) : (
            <AdminConfigLockedField label="Source" value={formData.source} isView={false} />
          )}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Priority <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                id="fsr-priority"
                value={formData.priority || ''}
                onChange={(e) => handleChange('priority', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.priority))}
              >
                <option value="">Select Priority</option>
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.priority ? <AdminFormFieldError>{errors.priority}</AdminFormFieldError> : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Severity Hint <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                id="fsr-severity"
                value={formData.severityHint || ''}
                onChange={(e) => handleChange('severityHint', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.severityHint))}
              >
                <option value="">Select Severity Hint</option>
                {SEVERITY_HINT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.severityHint ? <AdminFormFieldError>{errors.severityHint}</AdminFormFieldError> : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Info Request <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                id="fsr-info-request"
                value={formData.infoRequest || ''}
                onChange={(e) => handleChange('infoRequest', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.infoRequest))}
              >
                <option value="">Select Mode</option>
                {MODE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.infoRequest ? <AdminFormFieldError>{errors.infoRequest}</AdminFormFieldError> : null}
          </div>
          <AdminConfigLockedField
            label="Response Period"
            value={responsePeriodFor(formData.infoRequest)}
            isView={false}
          />

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Restriction <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                id="fsr-restriction"
                value={formData.restriction || ''}
                onChange={(e) => handleChange('restriction', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.restriction))}
              >
                <option value="">Select Mode</option>
                {MODE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.restriction ? <AdminFormFieldError>{errors.restriction}</AdminFormFieldError> : null}
          </div>
          {restrictionApplies ? (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Duration <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <div className="relative w-full min-w-0">
                <select
                  id="fsr-duration"
                  value={formData.duration || ''}
                  onChange={(e) => handleChange('duration', e.target.value)}
                  className={adminFormDetailSelectClasses(false, Boolean(errors.duration))}
                >
                  <option value="">Select Duration</option>
                  {DURATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
              {errors.duration ? <AdminFormFieldError>{errors.duration}</AdminFormFieldError> : null}
            </div>
          ) : (
            <AdminConfigLockedField label="Duration" value="—" isView={false} />
          )}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Protection <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                id="fsr-protection"
                value={formData.protection || ''}
                onChange={(e) => handleChange('protection', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.protection))}
              >
                <option value="">Select Mode</option>
                {MODE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.protection ? <AdminFormFieldError>{errors.protection}</AdminFormFieldError> : null}
          </div>
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>Protection Actions</AdminDetailFieldLabel>
            {protectionApplies ? (
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(formData.signOutSessions)}
                  onChange={(e) => handleChange('signOutSessions', e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                />
                <span className="text-offblack text-sm">{PROTECTION_ACTION}</span>
              </label>
            ) : (
              <AdminDetailFieldValue>—</AdminDetailFieldValue>
            )}
            {errors.signOutSessions ? <AdminFormFieldError>{errors.signOutSessions}</AdminFormFieldError> : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>Enforcement</AdminDetailFieldLabel>
            <div className="space-y-2">
              <label className="flex items-start gap-2 opacity-60">
                <input type="checkbox" checked disabled className="w-4 h-4 mt-0.5 text-primaryblue shrink-0" />
                <span className="text-offblack text-sm">Permanent Ban</span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(formData.finalWarning)}
                  onChange={(e) => handleChange('finalWarning', e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                />
                <span className="text-offblack text-sm">Final Warning</span>
              </label>
            </div>
            {errors.permanentBan ? <AdminFormFieldError>{errors.permanentBan}</AdminFormFieldError> : null}
          </div>
        </div>

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Optional Conditions</AdminPanelTitle>
          <AdminConfigLockedField label="Conditions" value="No Conditions" isView={false} />
          {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
        </div>

        <div className="space-y-3">
          <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
          {validationMessages.map((message) => (
            <AdminFormFieldError key={message} className="mb-0">
              {message}
            </AdminFormFieldError>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Cancel
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default FraudSecurityRuleDetail;
